import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LoginResponse, OrgBrief, RegisterPayload } from './auth.types';
import { SessionService, MeResponse } from '../core/session.service';
import { ActualiteService } from '../core/services/actualite.service';
import { LanguageService } from '../core/services/language.service';

const TOKEN_KEY = 'waitora_token';
const SKEW_SEC = 30; // marge pour éviter le « pile à l’expiration »

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly state$ = new BehaviorSubject<LoginResponse | null>(null);
  readonly auth$ = this.state$.asObservable();
  private loggingOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private session: SessionService,
    private actualiteSvc: ActualiteService,
    private injector: Injector,
  ) {}

  /**
   * Résolution paresseuse (via Injector, pas en champ de constructeur) : LanguageService.use()
   * déclenche un appel HTTP dès sa construction, et l'intercepteur de cet appel réinjecte
   * AuthService — une injection directe créerait donc un cycle NG0200 au bootstrap.
   */
  private get languageService(): LanguageService {
    return this.injector.get(LanguageService);
  }

  // ---------- état / token ----------
  private setState(v: LoginResponse | null) {
    this.state$.next(v);
  }

  getPermissions(): string[] {
    return this.state$.getValue()?.permissions ?? [];
  }

  hasPermission(permission: string): boolean {
    return this.getPermissions().includes(permission);
  }

  get currentUserEmail(): string | null {
    return this.state$.getValue()?.user?.email ?? null;
  }

  get currentOrg(): OrgBrief | null {
    return this.state$.getValue()?.org ?? null;
  }

  get currentUserInitials(): string {
    const user = this.state$.getValue()?.user;
    const first = user?.firstName?.[0] ?? '';
    const last = user?.lastName?.[0] ?? '';
    return (first + last).toUpperCase() || '?';
  }

  // ---------- token management (localStorage - XSS vulnerable) ----------
  private writeToken(token: string | null) {
    // WARNING: localStorage is vulnerable to XSS attacks
    // TODO: Migrate to httpOnly cookies when backend supports it
    try {
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch {}
  }

  tokenSnapshot(): string | null {
    const s = this.state$.getValue();
    return s?.token ?? this.readToken();
  }

  readToken(): string | null {
    // WARNING: Reading from localStorage exposes tokens to XSS attacks
    try { return localStorage.getItem(TOKEN_KEY); }
    catch { return null; }
  }

  // ---------- validation JWT ----------
  private decodeJwtExp(token: string): number | null {
    try {
      const payload = JSON.parse(
        atob((token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/'))
      );
      return typeof payload?.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  /** public pour les guards */
  isTokenExpired(token: string, skewSec = SKEW_SEC): boolean {
    const exp = this.decodeJwtExp(token);
    if (!exp) return true; // si pas d’exp, on considère pas valide côté client
    const now = Math.floor(Date.now() / 1000);
    return exp <= now + skewSec;
  }

  hasValidSession(): boolean {
    const t = this.tokenSnapshot();
    return !!t && !this.isTokenExpired(t);
  }

  get isLoggingOut() { return this.loggingOut; }

  // ---------- API ----------
  login(email: string, password: string): Observable<LoginResponse> {
    const url = `${environment.apiUrl}/api/v1/auth/login`;
    return this.http.post<LoginResponse>(url, { email, password }).pipe(
      tap((res) => {
        this.writeToken(res.token);
        this.setState(res);
        this.languageService.syncFromServer(res.user?.locale);
      })
    );
  }

  register(payload: RegisterPayload): Observable<LoginResponse> {
    const url = `${environment.apiUrl}/api/v1/auth/register`;
    return this.http.post<LoginResponse>(url, payload).pipe(
      tap((res) => {
        this.writeToken(res.token);
        this.setState(res);
        this.languageService.syncFromServer(res.user?.locale);
      })
    );
  }

  /** Reconstitue l’état depuis /me si un token existe déjà */
  me(): Observable<LoginResponse | null> {
    const token = this.readToken();
    if (!token) {
      this.setState(null);
      return of(null);
    }

    // déjà expiré → logout direct
    if (this.isTokenExpired(token)) {
      this.logout(true);
      return of(null);
    }

    const url = `${environment.apiUrl}/api/v1/auth/me`;
    return this.http.get<LoginResponse>(url).pipe(
      tap((res) => {
        this.setState({ ...res, token }); // /me ne renvoie pas le token
        this.languageService.syncFromServer(res.user?.locale);
      }),
      catchError((err: HttpErrorResponse) => {
        if ([401, 403, 419, 440].includes(err.status)) this.logout(true);
        return of(null);
      })
    );
  }

  /** À déclencher au bootstrap de l’app */
  bootstrap(): Observable<unknown> {
    const token = this.readToken();
    if (!token) {
      this.setState(null);
      return of(true);
    }
    if (this.isTokenExpired(token)) {
      this.logout(true);
      return of(true);
    }
    return this.me().pipe(
      tap((res) => {
        // Réutilise la réponse /auth/me déjà récupérée pour hydrater SessionService,
        // évite le second appel réseau que faisait ShellComponent.ngOnInit() via loadMe().
        if (res) this.session.hydrateFromAuthMe(res as unknown as MeResponse);
      }),
      map(() => true)
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    const url = `${environment.apiUrl}/api/v1/auth/forgot-password`;
    return this.http.post<{ message: string }>(url, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    const url = `${environment.apiUrl}/api/v1/auth/reset-password`;
    return this.http.post<{ message: string }>(url, { token, newPassword });
  }

  logout(sessionExpired = false): void {
    if (this.loggingOut) return;
    this.loggingOut = true;

    try { 
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    this.setState(null);
    this.session.reset();
    this.actualiteSvc.clearAllCaches();

    const url = sessionExpired ? '/login?expired=1' : '/login';
    this.router.navigateByUrl(url)
      .finally(() => { this.loggingOut = false; });
  }

  refreshPermissions(): Observable<LoginResponse | null> {
    return this.me();
  }

}
